import * as React from 'react';

import BookingService from '../../../domain/entities/BookingService';
import Merchant from '../../../domain/entities/Merchant';
import Review from '../../../domain/entities/Review';
import { filterXSS } from 'xss';

interface Props {
  breadcrumbs: Array<{ url: string; text: string }>;
  bookingService?: BookingService;
  merchant?: Merchant;
  reviews?: Review[];
}

function escape(str: string): string {
  return filterXSS(str);
}

export default class JsonLd extends React.PureComponent<Partial<Props>> {
  public render() {
    const jsonLdString = this.makeJsonLdString();
    if (jsonLdString === '') {
      return null;
    }
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLdString }}
      />
    );
  }

  private makeJsonLdString(): string {
    const structureData = [];

    if (this.props.merchant && !this.props.merchant.metaSetting.isNoIndex) {
      // localBusiness
      const merchantBusinessData = this.structureBusiness(
        this.props.merchant,
        this.props.reviews,
      );
      if (merchantBusinessData) {
        structureData.push(merchantBusinessData);
      }

      // aggregateRating
      if (
        this.props.merchant.setting.isCustomerReviewActive &&
        this.props.reviews
      ) {
        structureData.push(
          this.structureAggregateRating(
            this.props.merchant,
            this.props.bookingService,
          ),
        );
      }

      // product
      if (this.props.bookingService && this.props.bookingService.lowestPrice) {
        structureData.push(
          this.structureProduct(
            this.props.merchant,
            this.props.bookingService,
            this.props.reviews,
          ),
        );
      }
    }

    // breadcrumbs
    if (this.props.breadcrumbs && this.props.breadcrumbs.length > 0) {
      structureData.push(this.structureBreadcrumbs(this.props.breadcrumbs));
    }

    try {
      return JSON.stringify(structureData);
    } catch (_) {
      return '';
    }
  }

  private structureBreadcrumbs(
    breadcrumbs: Array<{ url: string; text: string }>,
  ) {
    return {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, i) => {
        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@id': `${item.url}#breadcrumb${i + 1}`,
            name: escape(item.text),
          },
        };
      }),
    };
  }

  private structureAggregateRating(
    merchant: Merchant,
    bookingService?: BookingService,
  ) {
    return {
      '@context': 'https://schema.org/',
      '@type': 'AggregateRating',
      itemReviewed: this.structureItemReviewed(merchant, bookingService),
      ratingValue: merchant.rating.average,
      ratingCount: merchant.rating.totalCount,
    };
  }

  private structureItemReviewed(
    merchant: Merchant,
    bookingService?: BookingService,
  ) {
    const image =
      bookingService &&
      bookingService.coverContent &&
      bookingService.coverContent.image.square
        ? bookingService.coverContent.image.square
        : merchant.coverImage;
    return {
      '@type': 'LocalBusiness',
      image: `${image.url}`,
      name: escape(
        `${bookingService ? bookingService.name : merchant.displayName}`,
      ),
      address: escape(`${merchant.address}`),
      telephone: escape(`${merchant.phoneNumber}`),
    };
  }

  private structureBusinessHours(merchant: Merchant) {
    const specs: [object?] = [];
    const wdays = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    merchant.businessHours.forEach(bh => {
      if (bh.isOpen) {
        specs.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [wdays[bh.weekday]],
          opens: bh.openingHour.substring(0, 5),
          closes: bh.closingHour.substring(0, 5),
        });
      }
    });
    if (specs.length === 0) {
      return null;
    }
    return specs;
  }

  private structureBusiness(merchant: Merchant, reviews?: Review[]) {
    const aggregateRatingData =
      merchant.setting.isCustomerReviewActive && reviews
        ? this.structureAggregateRating(merchant)
        : null;
    const merchantUrl = `https://coubic.com/${merchant.publicId}`;

    return {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': merchantUrl,
      image: [`${merchant.coverImage.url}`],
      name: escape(`${merchant.displayName}`),
      address: escape(`${merchant.address}`),
      telephone: `${merchant.phoneNumber}`,
      aggregateRating: aggregateRatingData,
      url: merchantUrl,
      description: escape(merchant.description),
      openingHoursSpecification: this.structureBusinessHours(merchant),
    };
  }

  private structureProduct(
    merchant: Merchant,
    bookingService: BookingService,
    reviews?: Review[],
  ) {
    const aggregateRatingData =
      merchant.setting.isCustomerReviewActive && reviews
        ? this.structureAggregateRating(merchant)
        : null;
    const image =
      bookingService &&
      bookingService.coverContent &&
      bookingService.coverContent.image.square
        ? bookingService.coverContent.image.square
        : merchant.coverImage;
    const bookingUrl = `https://coubic.com/${merchant.publicId}/${bookingService.publicId}`;

    return {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: escape(`${bookingService.name}`),
      description: escape(`${bookingService.description}`),
      image: [image.url],
      brand: {
        '@type': 'Thing',
        name: escape(`${merchant.displayName}`),
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'JPY',
        price: bookingService.lowestPrice || null,
        availability: 'PreOrder',
        url: bookingUrl,
      },
      aggregateRating: aggregateRatingData,
    };
  }
}
